import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Footer from '../../app/Footer';
import PaSurveyHeader from '../../app/PaSurveyHeader';
import il1 from './assets/DoctorPatient.svg';
import './PaAthenaInfoCheck.scss';
import { AthenaServices } from '../../lib/athena/athena-services';
import { produce } from 'immer';
import { usePaSurveyParams, useSurveyState, AidState } from '../hooks/use-survey-state';
import { useReportProviderState } from '../hooks/use-report';
import { useRecoilState } from 'recoil';
import { useStaticLocales } from '../../admin/hooks/locales/use-static-locale';
import PatientOrgContainer from '../hooks/PatientOrgContainer';
import { getPatientBasePath } from '../../app/route-utils';

const PaAthenaInfoCheck: React.FC = () => {
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCheckingEncounter, setIsCheckingEncounter] = useState(false);
  const [checkCount, setCheckCount] = useState(0);
  const navigate = useNavigate();
  const { orgId, shortId } = useParams();
  const [{ reportState, patientDetails }, { setReportState, setSurveyState, startSurvey }] = useSurveyState();
  const [, { resetAllStates }] = useReportProviderState();
  const [aid, setAid] = useRecoilState(AidState);
  const encounterCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousAidRef = useRef<string | null>(null);
  const { sltStr } = useStaticLocales();

  const searchParams = new URLSearchParams(window.location.search);

  const handleConfirm = async () => {
    if (!lastName.trim() || !birthDate.trim()) {
      setError(sltStr({ key: 'msg-please-fill-all-fields' }));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // aid가 query parameter로 들어오는 경우를 처리
      // 예: /athena-check?aid=xxxx 형태로 접근 시
      // URLSearchParams를 사용하여 aid를 추출
      const urlAid = searchParams.get('aid');
      const eid = searchParams.get('eid');

      if (!urlAid) {
        throw new Error(sltStr({ key: 'msg-appointment-id-required' }));
      }

      // localStorage에서 이전 aid 가져오기
      const previousAid = localStorage.getItem('currentAid');
      
      
      // aid가 변경되었으면 localStorage 정리 및 상태 초기화
      if (urlAid !== previousAid) {
        
        try {
          // recoil-persist로 저장된 모든 설문 관련 상태들 정리
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (
              key.includes('AdminPatientSurveyState') || 
              key.includes('PaReportState') ||
              key.includes('PaSurveyState') ||
              key.includes('PaErrorState') ||
              key.includes('AidState')
            )) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
          
          // Recoil 상태들도 초기화
          resetAllStates();
          
        } catch (e) {
          console.warn('localStorage 정리 중 오류:', e);
        }
      }

      // aid를 global state와 localStorage에 저장
      setAid(urlAid);
      localStorage.setItem('currentAid', urlAid);
      
      // Athena에서 환자 상세 정보 조회
      const result = await AthenaServices.checkInfo(urlAid, lastName, birthDate);

      if (result.success) {
        const firstPatient = result.patient;
        const { checkFlag } = result;

        setSurveyState((st) =>
          produce(st, (dr) => {
            dr.checkFlag = checkFlag;
          }),
        );

        setSurveyState((st) =>
          produce(st, (dr) => {
            dr.patientDetails = result.patient;
          }),
        );

        setReportState((st) =>
          produce(st, (dr) => {
            if (!dr?.systemValueMap) dr.systemValueMap = {};

            // 환자 기본 정보 추가
            if (firstPatient.dob) {
              dr.systemValueMap.dateOfBirth = firstPatient.dob;
            }
            if (firstPatient.lastName) {
              dr.systemValueMap.lastName = firstPatient.lastName;
            }
            if (firstPatient.firstName) {
              dr.systemValueMap.firstName = firstPatient.firstName;
            }
            if (firstPatient.middleName) {
              dr.systemValueMap.middleName = firstPatient.middleName;
            }
            if (firstPatient.gender) {
              dr.systemValueMap.gender = firstPatient.gender;
            }
            if (firstPatient.phoneNumber) {
              dr.systemValueMap.phoneNumber = firstPatient.phoneNumber;
            }
            if (firstPatient.email) {
              dr.systemValueMap.email = firstPatient.email;
            }
            if (firstPatient.address) {
              dr.systemValueMap.address = firstPatient.address;
            }
            if (firstPatient.city) {
              dr.systemValueMap.city = firstPatient.city;
            }
            if (firstPatient.state) {
              dr.systemValueMap.state = firstPatient.state;
            }
            if (firstPatient.zipCode) {
              dr.systemValueMap.zipCode = firstPatient.zipCode;
            }
          }),
        );

        // encounter ID를 surveyState에 저장하여 AppRoutesPatient에서 체크 시작
        setSurveyState((st) =>
          produce(st, (dr) => {
            dr.encounterId = eid ?? '';
            dr.encounterCheckStatus = 'checking';
            dr.encounterCheckReason = undefined; // 초기화
          }),
        );

        // 설문 시작 페이지로 이동 (AppRoutesPatient에서 encounter 체크가 시작됨)
        // aid 파라미터를 URL에 포함시켜서 이동
        navigate(`${getPatientBasePath(window.location.pathname)}/${orgId}/${shortId}?aid=${urlAid}&eid=${eid ?? ''}`);
      } else {
        setError(sltStr({ key: 'msg-patient-not-found' }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : sltStr({ key: 'msg-unknown-error' }));
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (value: string) => {
    // Format as MM/DD/YYYY
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDate(e.target.value);
    setBirthDate(formatted);
  };

  return (
    <PatientOrgContainer.Provider>
      <div className='flex-col flex-1 w-full gap-5 px-5 pt-2'>
      <div className='flex-col flex-1 w-full gap-6'>
        <PaSurveyHeader />

        <div className='text-xl flex-col gap-3'>
          <div className='' />

          <div className='h-1' />

          <h4 className='font-bold sm'>{sltStr({ key: 'title-verify-patient' })}</h4>
          <p className='md text-light'>{sltStr({ key: 'desc-verify-patient' })}</p>
        </div>

        {/* Patient Last Name Section */}
        <div className='form-control'>
          <label className='form-label'>
            {sltStr({ key: 'label-patient-last-name' })}
          </label>
          <div className='hint-text text-xs opacity-70'>{sltStr({ key: 'hint-legal-name-english' })}</div>
          <input
            type='text'
            className='input input-bordered w-full'
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={sltStr({ key: 'placeholder-enter-last-name' })}
            disabled={isLoading}
          />
        </div>

        {/* Patient Birth Date Section */}
        <div className='form-control'>
          <label className='form-label'>
            {sltStr({ key: 'label-patient-birth-date' })}
          </label>
          <div className='hint-text text-xs opacity-70'>{sltStr({ key: 'hint-month-date-year' })}</div>
          <input
            type='text'
            className='input input-bordered w-full'
            value={birthDate}
            onChange={handleDateChange}
            placeholder={sltStr({ key: 'placeholder-mm-dd-yyyy' })}
            maxLength={10}
            disabled={isLoading}
          />
        </div>

        {/* Error Message */}
        {error && (
          <p className='text-error text-sm -mt-3'>{error}</p>
        )}

        {/* Confirm Button */}
        <div className='flex-col gap-2'>
          <button
            className='btn btn-lg btn-primary w-full'
            onClick={handleConfirm}
            disabled={isLoading || !lastName.trim() || !birthDate.trim()}
          >
            {isLoading ? sltStr({ key: 'btn-checking' }) : sltStr({ key: 'btn-confirm' })}
          </button>
        </div>
      </div>
      <Footer />
    </div>
    </PatientOrgContainer.Provider>
  );
};

export default PaAthenaInfoCheck; 
